import {
  breakpoints,
  spacingValues,
  fontSizes,
  fontWeights,
  lineHeights,
  displayValues,
  alignItemsValues,
  justifyContentValues,
  themes,
  flexDirectionValues,
  textAlignValues,
  resolveThemeColorValues,
} from './variables';
import { DEFAULT_THEME_COLOR } from '../../utils/themeColor';

const breakpointValue = Object.entries(breakpoints);

// Theme colour tokens are declared per picked colour, so their names are read from the default one.
const COLOR_TOKEN_NAMES = Object.keys({
  ...themes.light,
  ...resolveThemeColorValues(DEFAULT_THEME_COLOR, 'light'),
});

const colorUtilities = COLOR_TOKEN_NAMES.map(
  (color) => `
  .text-${color} {
    color: var(--${color}) !important;
  }
  .bg-${color} {
    background-color: var(--${color}) !important;
  }
  .${color} {
    --color: var(--${color});
  }
`,
).join('');

/**
 * `gutter` resolves to the responsive outer spacing token (10px on a narrow task pane,
 * 15px once it is wide enough), so `.py-gutter` stays in step with the page padding.
 */
const GUTTER_SUFFIX = 'gutter';
const GUTTER_LENGTH = 'var(--spacing-gutter)';

const DATE_GROUP_SUFFIX = 'date-group';
const DATE_GROUP_LENGTH = 'var(--spacing-date-group)';

const spacingRules = (suffix: string | number, length: string) => `
  .p-${suffix} {
    padding: ${length} !important;
  }
  .px-${suffix} {
    padding-inline: ${length} !important;
  }
  .py-${suffix} {
    padding-block: ${length} !important;
  }
  .pl-${suffix} {
    padding-inline-start: ${length} !important;
  }
  .pr-${suffix} {
    padding-inline-end: ${length} !important;
  }
  .pt-${suffix} {
    padding-block-start: ${length} !important;
  }
  .pb-${suffix} {
    padding-block-end: ${length} !important;
  }
  .m-${suffix} {
    margin: ${length} !important;
  }
  .mx-${suffix} {
    margin-inline: ${length} !important;
  }
  .my-${suffix} {
    margin-block: ${length} !important;
  }
  .ml-${suffix} {
    margin-inline-start: ${length} !important;
  }
  .mr-${suffix} {
    margin-inline-end: ${length} !important;
  }
  .mt-${suffix} {
    margin-block-start: ${length} !important;
  }
  .mb-${suffix} {
    margin-block-end: ${length} !important;
  }
`;

const spacingUtilities = [
  ...spacingValues.map((value) => spacingRules(value, `${value}px`)),
  spacingRules(GUTTER_SUFFIX, GUTTER_LENGTH),
].join('\n');

const responsiveSpacing = breakpointValue
  .flatMap(([size, value]) =>
    spacingValues.map(
      (space) => `
    @media ${value}{
    .p-${size}-${space} {
      padding: ${space}px !important;
    }
    .px-${size}-${space} {
      padding-inline: ${space}px !important;
    }
    .py-${size}-${space} {
      padding-block: ${space}px !important;
    }
    .pl-${size}-${space} {
      padding-inline-start: ${space}px !important;
    }
    .pr-${size}-${space} {
      padding-inline-end: ${space}px !important;
    }
    .pt-${size}-${space} {
      padding-block-start: ${space}px !important;
    }
    .pb-${size}-${space} {
      padding-block-end: ${space}px !important;
    }
    .m-${size}-${space} {
      margin: ${space}px !important;
    }
    .mx-${size}-${space} {
      margin-inline: ${space}px !important;
    }
    .my-${size}-${space} {
      margin-block: ${space}px !important;
    }
    .ml-${size}-${space} {
      margin-inline-start: ${space}px !important;
    }
    .mr-${size}-${space} {
      margin-inline-end: ${space}px !important;
    }
    .mt-${size}-${space} {
      margin-block-start: ${space}px !important;
    }
    .mb-${size}-${space} {
      margin-block-end: ${space}px !important;
    }
  }
  `,
    ),
  )
  .join('');

const textAlignUtilities = textAlignValues
  .map(
    (value) => `
.ta-${value} {
  text-align: ${value} !important;
}
`,
  )
  .join('\n');

const responsiveTextAlign = breakpointValue
  .flatMap(([size, value]) =>
    textAlignValues.map(
      (textAlign) => `
  @media ${value}{
  .ta-${size}-${textAlign} {
    text-align: ${textAlign} !important;
  }
}
`,
    ),
  )
  .join('');

const fontUtilities = fontSizes
  .map(
    (value) => `
.fz-${value} {
  font-size: ${value}px !important;
}
`,
  )
  .join('\n');

const responsiveFonts = breakpointValue
  .flatMap(([size, value]) =>
    fontSizes.map(
      (fontSize) => `
  @media ${value}{
  .fz-${size}-${fontSize} {
    font-size: ${fontSize}px !important;
  }
}
`,
    ),
  )
  .join('');

const lineHeightUtilities = lineHeights
  .map(
    (value) => `
.lh-${value} {
  line-height: ${value}px !important;
}
`,
  )
  .join('\n');

const responsiveLineHeights = breakpointValue
  .flatMap(([size, value]) =>
    lineHeights.map(
      (lineHeight) => `
  @media ${value}{
  .lh-${size}-${lineHeight} {
    line-height: ${lineHeight}px !important;
  }
}
`,
    ),
  )
  .join('');

const fontWeightUtilities = fontWeights
  .map(
    (value) => `
.fw-${value} {
  font-weight: ${value} !important;
}
`,
  )
  .join('\n');

const displayUtilities = displayValues
  .map(
    (value) => `
.d-${value} {
  display: ${value} !important;
}
`,
  )
  .join('\n');

const responsiveDisplay = breakpointValue
  .flatMap(([size, value]) =>
    displayValues.map(
      (display) => `
  @media ${value}{
  .d-${size}-${display} {
    display: ${display} !important;
  }
}
`,
    ),
  )
  .join('');

const alignItemsUtilities = alignItemsValues
  .map(
    (value) => `
.ai-${value} {
  align-items: ${value} !important;
}
`,
  )
  .join('\n');

const responsiveAlignItems = breakpointValue
  .flatMap(([size, value]) =>
    alignItemsValues.map(
      (alignment) => `
  @media ${value}{
  .ai-${size}-${alignment} {
    align-items: ${alignment} !important;
  }
}
`,
    ),
  )
  .join('');

const justifyContentUtilities = justifyContentValues
  .map(
    (value) => `
.jc-${value} {
  justify-content: ${value} !important;
}
`,
  )
  .join('\n');

const responsiveJustifyContent = breakpointValue
  .flatMap(([size, value]) =>
    justifyContentValues.map(
      (justification) => `
  @media ${value}{
  .jc-${size}-${justification} {
    justify-content: ${justification} !important;
  }
}
`,
    ),
  )
  .join('');

const gapRules = (suffix: string | number, length: string) => `
.gap-${suffix} {
  gap: ${length} !important;
}
`;

const gapUtilities = [
  ...spacingValues.map((value) => gapRules(value, `${value}px`)),
  gapRules(GUTTER_SUFFIX, GUTTER_LENGTH),
  gapRules(DATE_GROUP_SUFFIX, DATE_GROUP_LENGTH),
].join('\n');

const responsiveGap = breakpointValue
  .flatMap(([size, value]) =>
    spacingValues.map(
      (gap) => `
  @media ${value}{
  .gap-${size}-${gap} {
    gap: ${gap}px !important;
  }
}
`,
    ),
  )
  .join('');

const flexDirectionUtilities = flexDirectionValues
  .map(
    (value) => `
.fd-${value} {
  flex-direction: ${value} !important;
}
`,
  )
  .join('\n');

const responsiveFlexDirection = breakpointValue
  .flatMap(([size, value]) =>
    flexDirectionValues.map(
      (direction) => `
  @media ${value}{
  .fd-${size}-${direction} {
    flex-direction: ${direction} !important;
  }
}
`,
    ),
  )
  .join('');

export const utilityClasses = `
  ${colorUtilities},
  ${spacingUtilities},
  ${responsiveSpacing},
  ${fontUtilities},
  ${responsiveFonts},
  ${lineHeightUtilities},
  ${responsiveLineHeights},
  ${fontWeightUtilities},
  ${displayUtilities},
  ${responsiveDisplay},
  ${alignItemsUtilities},
  ${responsiveAlignItems},
  ${justifyContentUtilities},
  ${responsiveJustifyContent},
  ${gapUtilities},
  ${responsiveGap},
  ${flexDirectionUtilities},
  ${responsiveFlexDirection},
  ${textAlignUtilities},
  ${responsiveTextAlign}
`
  .replace(/\s+/g, ' ')
  .trim();
